import React, { useEffect, useState, useMemo } from 'react';
import { useOutletContext } from 'react-router-dom';
import pb from '../../lib/pocketbase';
import styles from '../Dashboard.module.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faPlus, faEdit, faTrash, faPhone, faEnvelope, faSearch,
    faUserTie, faBuilding, faMapMarkerAlt, faInfoCircle, faUserPlus,
    faCheck, faCheckCircle
} from '@fortawesome/free-solid-svg-icons';
import { toast } from 'react-toastify';
import CustomerForm from '../../components/dashboard/CustomerForm';

const Customers = () => {
    const { userRole } = useOutletContext();
    const [customers, setCustomers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [typeFilter, setTypeFilter] = useState('all');

    // Modal State
    const [isFormOpen, setIsFormOpen] = useState(false);
    const [editingCustomer, setEditingCustomer] = useState(null);

    useEffect(() => {
        loadCustomers();
    }, []);

    const loadCustomers = async () => {
        try {
            setLoading(true);

            // 1. Fetch from customers collection
            const customerRecords = await pb.collection('customers').getFullList({
                sort: '-created',
            });

            // 2. Fetch from users collection (those with profile_type = 'client')
            const userClients = await pb.collection('users').getFullList({
                filter: 'profile_type = "client"',
                sort: '-created',
            });

            // 3. Merge and De-duplicate by email
            // We prioritize data from 'customers' collection if it exists
            const mergedMap = new Map();

            // First add users as potential customers
            userClients.forEach(u => {
                const emailKey = (u.email || '').toLowerCase();
                if (emailKey) {
                    mergedMap.set(emailKey, {
                        id: u.id,
                        name: u.name || u.username || 'Sans nom',
                        email: u.email,
                        phone: u.phone || '',
                        adress: u.adress || '',
                        notes: u.notes || 'Compte utilisateur actif',
                        type: 'Particulier', // Default for users unless specified
                        isUser: true,
                        created: u.created
                    });
                }
            });

            // Then add/overwrite with specific CRM customer records
            customerRecords.forEach(c => {
                const emailKey = (c.email || '').toLowerCase();
                if (emailKey) {
                    mergedMap.set(emailKey, {
                        ...c,
                        isCRM: true
                    });
                } else {
                    // No email? Add by ID to avoid losing them
                    mergedMap.set(`id_${c.id}`, {
                        ...c,
                        isCRM: true
                    });
                }
            });

            const finalRecords = Array.from(mergedMap.values()).sort((a, b) =>
                new Date(b.created) - new Date(a.created)
            );

            setCustomers(finalRecords);
        } catch (err) {
            console.error("Erreur chargement clients", err);
            toast.error("Impossible de charger les clients");
        } finally {
            setLoading(false);
        }
    };

    const handleInviteClient = async (customer) => {
        if (!customer.email) {
            toast.error("Ce client n'a pas d'adresse email renseignée.");
            return;
        }

        if (!window.confirm(`Créer un accès Espace Client pour ${customer.name} (${customer.email}) ?`)) return;

        try {
            // Check if user already exists
            const existing = await pb.collection('users').getList(1, 1, {
                filter: `email = "${customer.email}"`
            });

            if (existing.items.length > 0) {
                toast.warning("Un utilisateur avec cet email possède déjà un accès.");
                return;
            }

            // Create User account
            const tempPassword = Math.random().toString(36).slice(-10) + "Aa1!";
            const userData = {
                username: customer.name.toLowerCase().replace(/\s/g, '_') + Math.floor(Math.random() * 1000),
                email: customer.email,
                password: tempPassword,
                passwordConfirm: tempPassword,
                name: customer.name,
                role: 'client',
            };

            await pb.collection('users').create(userData);
            toast.success(`Accès client créé avec succès pour ${customer.name} !`);

        } catch (error) {
            console.error("Client inviting error:", error);
            toast.error("Erreur lors de la création de l'accès client.");
        }
    };

    const filteredCustomers = useMemo(() => {
        return customers.filter(c => {
            const matchesSearch =
                c.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                (c.email && c.email.toLowerCase().includes(searchTerm.toLowerCase())) ||
                (c.phone && c.phone.includes(searchTerm));
            const matchesType = typeFilter === 'all' || c.type === typeFilter;
            return matchesSearch && matchesType;
        });
    }, [customers, searchTerm, typeFilter]);

    const stats = useMemo(() => {
        return {
            total: customers.length,
            pros: customers.filter(c => c.type === 'Professionnel' || c.type === 'Architecte').length,
            particuliers: customers.filter(c => c.type === 'Particulier' || !c.type).length,
            newThisMonth: customers.filter(c => {
                const date = new Date(c.created);
                const now = new Date();
                return date.getMonth() === now.getMonth() && date.getFullYear() === now.getFullYear();
            }).length
        };
    }, [customers]);

    const handleCreate = () => {
        setEditingCustomer(null);
        setIsFormOpen(true);
    };

    const handleEdit = (customer) => {
        setEditingCustomer(customer);
        setIsFormOpen(true);
    };

    const handleDelete = async (id) => {
        if (!window.confirm("Êtes-vous sûr de vouloir supprimer ce client ?")) return;
        try {
            await pb.collection('customers').delete(id);
            toast.success("Client retiré de la base");
            loadCustomers();
        } catch (err) {
            toast.error("Erreur à la suppression");
        }
    };

    const handleSave = () => {
        setIsFormOpen(false);
        loadCustomers();
    };

    const getTypeBadgeStyle = (type) => {
        switch (type) {
            case 'Professionnel': return { backgroundColor: '#e0f2fe', color: '#0369a1' };
            case 'Architecte': return { backgroundColor: '#f5f3ff', color: '#5b21b6' };
            case 'Collectivité': return { backgroundColor: '#fef2f2', color: '#991b1b' };
            default: return { backgroundColor: '#f0fdf4', color: '#166534' };
        }
    };

    return (
        <div className={styles.dataSection}>
            <div className={styles.sectionHeader}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                    <h3 style={{ margin: 0 }}><FontAwesomeIcon icon={faUserTie} /> Base Clients CRM</h3>
                    <button className={styles.addButton} onClick={handleCreate}>
                        <FontAwesomeIcon icon={faPlus} /> Nouveau Client
                    </button>
                </div>

                <div className={styles.kpiRow}>
                    <div className={styles.kpiCard}>
                        <span>Total Clients</span>
                        <strong>{stats.total}</strong>
                    </div>
                    <div className={styles.kpiCard}>
                        <span>Pros / Archis</span>
                        <strong>{stats.pros}</strong>
                    </div>
                    <div className={styles.kpiCard}>
                        <span>Nouveaux (Mois)</span>
                        <strong>{stats.newThisMonth}</strong>
                    </div>
                </div>
            </div>

            <div style={{
                margin: '20px 0',
                display: 'flex',
                gap: '15px',
                flexWrap: 'wrap',
                background: 'var(--color-surface, white)',
                padding: '1.5rem',
                borderRadius: '12px',
                boxShadow: 'var(--shadow-sm)'
            }}>
                <div style={{ flex: 1, minWidth: '250px', position: 'relative' }}>
                    <input
                        type="text"
                        placeholder="Rechercher par nom, email ou téléphone..."
                        style={{
                            width: '100%',
                            padding: '12px 12px 12px 40px',
                            borderRadius: '10px',
                            border: '1px solid var(--color-light-gray, #e2e8f0)',
                            background: 'var(--color-background, white)',
                            color: 'var(--color-text)',
                            fontSize: '0.9rem'
                        }}
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                    <FontAwesomeIcon icon={faSearch} style={{ position: 'absolute', left: '15px', top: '16px', color: 'var(--color-text-light, #94a3b8)' }} />
                </div>

                <select
                    style={{ 
                        padding: '12px', 
                        borderRadius: '10px', 
                        border: '1px solid var(--color-light-gray, #e2e8f0)', 
                        background: 'var(--color-background, white)',
                        color: 'var(--color-text)',
                        minWidth: '180px', 
                        fontSize: '0.9rem' 
                    }}
                    value={typeFilter}
                    onChange={(e) => setTypeFilter(e.target.value)}
                >
                    <option value="all">Tous les types</option>
                    <option value="Particulier">Particuliers</option>
                    <option value="Professionnel">Professionnels</option>
                    <option value="Architecte">Architectes</option>
                    <option value="Collectivité">Collectivités</option>
                </select>
            </div>

            {loading ? (
                <div style={{ padding: '3rem', textAlign: 'center', color: '#64748b' }}>
                    <div className={styles.loader}></div>
                    <p>Chargement de la base clients...</p>
                </div>
            ) : (
                <div className={styles.tableContainer}>
                    <table className={styles.dataTable}>
                        <thead>
                            <tr>
                                <th>Client / Type</th>
                                <th>Contact</th>
                                <th>Localisation</th>
                                <th>Notes CRM</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredCustomers.map((customer) => (
                                <tr key={customer.id}>
                                    <td>
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                                            <div style={{ fontWeight: '800', fontSize: '1rem', color: 'var(--color-secondary)' }}>
                                                {customer.type === 'Professionnel' ? <FontAwesomeIcon icon={faBuilding} style={{ marginRight: '8px', fontSize: '0.8rem', color: '#64748b' }} /> : null}
                                                {customer.name}
                                            </div>
                                            <div style={{ display: 'flex', gap: '5px', alignItems: 'center' }}>
                                                <span className={styles.statusBadge} style={{ ...getTypeBadgeStyle(customer.type), width: 'fit-content' }}>
                                                    {customer.type || 'Particulier'}
                                                </span>
                                                {customer.isUser && (
                                                    <span className={styles.statusBadge} style={{ backgroundColor: '#f0fdf4', color: '#166534', border: '1px solid #bcf0da', fontSize: '0.65rem' }}>
                                                        <FontAwesomeIcon icon={faCheck} style={{ marginRight: '4px' }} /> Compte actif
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                    </td>
                                    <td>
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                            {customer.email && (
                                                <div style={{ fontSize: '0.85rem', color: 'var(--color-primary)', fontWeight: '600' }}>
                                                    <FontAwesomeIcon icon={faEnvelope} style={{ width: '16px', marginRight: '6px', opacity: 0.7 }} /> {customer.email}
                                                </div>
                                            )}
                                            {customer.phone && (
                                                <div style={{ fontSize: '0.85rem', color: '#64748b' }}>
                                                    <FontAwesomeIcon icon={faPhone} style={{ width: '16px', marginRight: '6px', opacity: 0.7 }} /> {customer.phone}
                                                </div>
                                            )}
                                        </div>
                                    </td>
                                    <td style={{ fontSize: '0.85rem', color: '#475569', maxWidth: '180px' }}>
                                        {customer.adress ? (
                                            <><FontAwesomeIcon icon={faMapMarkerAlt} style={{ marginRight: '6px', opacity: 0.5 }} /> {customer.adress}</>
                                        ) : <span style={{ fontStyle: 'italic', opacity: 0.5 }}>Non renseignée</span>}
                                    </td>
                                    <td style={{ maxWidth: '200px' }}>
                                        {customer.notes ? (
                                            <div style={{ fontSize: '0.8rem', color: '#64748b', display: 'flex', gap: '6px' }}>
                                                <FontAwesomeIcon icon={faInfoCircle} style={{ marginTop: '3px', opacity: 0.5 }} />
                                                <span style={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                                                    {customer.notes}
                                                </span>
                                            </div>
                                        ) : '-'}
                                    </td>
                                    <td className={styles.actions}>
                                        <div style={{ display: 'flex', gap: '5px' }}>
                                            <button
                                                className={styles.iconBtn}
                                                onClick={() => !customer.isUser && handleInviteClient(customer)}
                                                title={customer.isUser ? "Possède déjà un accès" : "Créer un accès Espace Client"}
                                                style={{
                                                    color: customer.isUser ? '#10b981' : 'var(--color-primary)',
                                                    cursor: customer.isUser ? 'default' : 'pointer',
                                                    opacity: customer.isUser ? 0.7 : 1
                                                }}
                                            >
                                                <FontAwesomeIcon icon={customer.isUser ? faCheckCircle : faUserPlus} />
                                            </button>
                                            <button className={styles.iconBtn} onClick={() => handleEdit(customer)} title="Modifier la fiche">
                                                <FontAwesomeIcon icon={faEdit} />
                                            </button>
                                            {userRole === 'admin' && (
                                                <button className={styles.iconBtn} style={{ color: '#ef4444' }} onClick={() => handleDelete(customer.id)} title="Supprimer le client">
                                                    <FontAwesomeIcon icon={faTrash} />
                                                </button>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            ))}
                            {filteredCustomers.length === 0 && (
                                <tr>
                                    <td colSpan="5" style={{ textAlign: 'center', padding: '4rem', color: '#94a3b8' }}>
                                        <FontAwesomeIcon icon={faSearch} size="2x" style={{ marginBottom: '1rem', opacity: 0.2 }} />
                                        <p>Aucun client trouvé pour cette recherche.</p>
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            )}

            {isFormOpen && (
                <CustomerForm
                    customer={editingCustomer}
                    onSave={handleSave}
                    onCancel={() => setIsFormOpen(false)}
                />
            )}
        </div>
    );
};

export default Customers;
